import { isMemoryArchiveType } from "@/lib/leaderMemoryVisibility";
import { normalizeBiConfidence } from "../../../../supabase/functions/_shared/bi-v2/retrievalLens";
import type { LatestBehavioralPattern } from "@/lib/behavioralPatternLedger";
import type {
  BehavioralThemeV2,
  ObservationClusterV2,
} from "../../../../supabase/functions/_shared/bi-v2/contracts";
import type { GraphSnapshot } from "../../../../supabase/functions/_shared/contextGraph";
import type { GraphNodeSummary } from "../../../../supabase/functions/_shared/contextGraph";
import type { BehavioralReadModel, BehavioralEvidenceRef, BehavioralConfidence } from "./contracts";
import {
  downgradeBehavioralConfidence,
  isThinBehavioralEvidence,
  normalizeBehavioralConfidence,
  normalizeBehavioralKey,
} from "./contracts";

function extractArtifactIds(value: unknown): string[] {
  if (typeof value === "string" && value.trim().length > 0) return [value.trim()];
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    .map((item) => item.trim());
}

function themeContradictionCount(themeName: string, clusters: ObservationClusterV2[]): number {
  return clusters
    .filter((cluster) => cluster.behavior_theme === themeName)
    .reduce((sum, cluster) => sum + Math.max(0, cluster.contradiction_count), 0);
}

function buildThemeRankingScore(theme: BehavioralThemeV2, contradictionCount: number): number {
  const recurrenceBonus = Math.min(0.2, Math.max(0, theme.supporting_meeting_count - 1) * 0.05);
  const artifactBonus = Math.min(0.1, Math.max(0, theme.supporting_artifact_count - 1) * 0.03);
  const contradictionPenalty = Math.min(0.3, contradictionCount * 0.15);
  return normalizeBiConfidence(theme.confidence_level) + recurrenceBonus + artifactBonus - contradictionPenalty;
}

function rankCanonicalThemes(themes: BehavioralThemeV2[], clusters: ObservationClusterV2[]): BehavioralThemeV2[] {
  return [...themes].sort((left, right) => {
    const leftContradictions = themeContradictionCount(left.theme_name, clusters);
    const rightContradictions = themeContradictionCount(right.theme_name, clusters);
    const scoreDelta = buildThemeRankingScore(right, rightContradictions) - buildThemeRankingScore(left, leftContradictions);
    if (scoreDelta !== 0) return scoreDelta;

    const leftTime = left.last_seen_at ? Date.parse(left.last_seen_at) : Number.NEGATIVE_INFINITY;
    const rightTime = right.last_seen_at ? Date.parse(right.last_seen_at) : Number.NEGATIVE_INFINITY;
    const recencyDelta = rightTime - leftTime;
    if (Number.isFinite(recencyDelta) && recencyDelta !== 0) return recencyDelta;

    return 0;
  });
}

function normalizeThemeConfidence(theme: BehavioralThemeV2): BehavioralConfidence {
  if (theme.confidence_level === "high" || theme.confidence_level === "medium" || theme.confidence_level === "low") {
    return theme.confidence_level;
  }
  if (theme.confidence_score >= 0.75) return "high";
  if (theme.confidence_score >= 0.45) return "medium";
  return "low";
}

function buildCanonicalEvidenceRefs(params: {
  theme: BehavioralThemeV2;
  clusters: ObservationClusterV2[];
  contextGraph?: GraphSnapshot | null;
}): BehavioralEvidenceRef[] {
  const linkedArtifactIds = new Set<string>();
  const themeMetadata = params.theme.metadata as Record<string, unknown>;
  extractArtifactIds(themeMetadata.last_source_artifact_id).forEach((id) => linkedArtifactIds.add(id));
  extractArtifactIds(themeMetadata.source_artifact_id).forEach((id) => linkedArtifactIds.add(id));
  extractArtifactIds(themeMetadata.supporting_artifact_ids).forEach((id) => linkedArtifactIds.add(id));

  const relatedClusters = params.clusters.filter((cluster) => cluster.behavior_theme === params.theme.theme_name);
  for (const cluster of relatedClusters) {
    const metadata = cluster.metadata as Record<string, unknown>;
    extractArtifactIds(metadata.last_source_artifact_id).forEach((id) => linkedArtifactIds.add(id));
    extractArtifactIds(metadata.source_artifact_id).forEach((id) => linkedArtifactIds.add(id));
    extractArtifactIds(metadata.supporting_artifact_ids).forEach((id) => linkedArtifactIds.add(id));
    extractArtifactIds(metadata.contradiction_source_artifact_id).forEach((id) => linkedArtifactIds.add(id));
  }

  const normalizedThemeName = normalizeBehavioralKey(params.theme.theme_name);
  const clusterSourceIds = new Set(
    relatedClusters.flatMap((cluster) => [cluster.id, cluster.merge_key].filter(Boolean)),
  );

  return (params.contextGraph?.recentNodes ?? [])
    .filter((node) => {
      if (isMemoryArchiveType(node.node_type) || isMemoryArchiveType(node.source_type)) return false;
      if (linkedArtifactIds.has(node.source_id)) return true;
      if (node.source_type === "behavioral_intelligence_v2_theme" && node.source_id === params.theme.theme_id) return true;
      if (node.source_type === "behavioral_intelligence_v2_cluster" && clusterSourceIds.has(node.source_id)) return true;

      const normalizedTitle = normalizeBehavioralKey(node.title ?? "");
      if (normalizedTitle.length === 0) return false;

      if (node.source_type === "behavioral_intelligence_v2_theme" || node.source_type === "behavioral_intelligence_v2_cluster") {
        return normalizedTitle === normalizedThemeName;
      }

      return false;
    })
    .sort((left, right) => {
      const leftTime = left.created_at ? Date.parse(left.created_at) : Number.NEGATIVE_INFINITY;
      const rightTime = right.created_at ? Date.parse(right.created_at) : Number.NEGATIVE_INFINITY;
      const recencyDelta = rightTime - leftTime;
      if (Number.isFinite(recencyDelta) && recencyDelta !== 0) return recencyDelta;
      return 0;
    })
    .slice(0, 6)
    .map((node) => ({
      id: node.id,
      label: node.title?.trim() || node.source_type,
      sourceType: node.source_type,
      sourceId: node.source_id,
      createdAt: node.created_at ?? null,
      route: node.coach_link,
      origin: "context_graph" as const,
    }));
}

type NormalizedBehavioralEvidenceRefs = {
  evidenceRefs: BehavioralEvidenceRef[];
  graphEvidenceCount: number;
  usedCompatibilityFallback: boolean;
};

function buildCompatibilityEvidenceRefs(
  compatibilitySummary: LatestBehavioralPattern,
): BehavioralEvidenceRef[] {
  return compatibilitySummary.evidenceMeetingIds.slice(0, 6).map((meetingId, index) => ({
    id: `compatibility-${meetingId}`,
    label: `Compatibility summary evidence ${index + 1}`,
    sourceType: "behavioral_intelligence_v1",
    sourceId: meetingId,
    createdAt: compatibilitySummary.createdAt ?? null,
    route: null,
    origin: "compatibility_only_v1" as const,
  }));
}

function buildContradictions(params: {
  behaviorKey: string;
  clusters: ObservationClusterV2[];
  evidenceRefs: BehavioralEvidenceRef[];
}): Array<{ behaviorKey: string; reason: string | null; source: "bi_v2"; evidenceRefId: string | null }> {
  return params.clusters
    .filter((cluster) => cluster.behavior_theme === params.behaviorKey && cluster.contradiction_count > 0)
    .sort((left, right) => {
      const leftTime = left.created_at ? Date.parse(left.created_at) : Number.NEGATIVE_INFINITY;
      const rightTime = right.created_at ? Date.parse(right.created_at) : Number.NEGATIVE_INFINITY;
      const recencyDelta = rightTime - leftTime;
      if (Number.isFinite(recencyDelta) && recencyDelta !== 0) return recencyDelta;

      const contradictionDelta = right.contradiction_count - left.contradiction_count;
      if (contradictionDelta !== 0) return contradictionDelta;

      return 0;
    })
    .map((cluster) => {
      const metadata = cluster.metadata as Record<string, unknown>;
      const reason = typeof metadata.contradiction_signal === "string" && metadata.contradiction_signal.trim().length > 0
        ? metadata.contradiction_signal.trim()
        : null;
      const contradictionArtifactId = extractArtifactIds(metadata.contradiction_source_artifact_id)[0]
        ?? extractArtifactIds(metadata.source_artifact_id)[0]
        ?? null;
      return {
        behaviorKey: params.behaviorKey,
        reason,
        source: "bi_v2" as const,
        evidenceRefId: contradictionArtifactId
          ? params.evidenceRefs.find((ref) => ref.sourceId === contradictionArtifactId)?.id ?? null
          : null,
      };
    });
}

export type BuildCoachVeeraBehavioralReadModelParams = {
  biThemes?: BehavioralThemeV2[] | null;
  biClusters?: ObservationClusterV2[] | null;
  contextGraph?: GraphSnapshot | null;
  compatibilitySummary?: LatestBehavioralPattern | null;
};

function buildCanonicalReadModel(params: {
  themes: BehavioralThemeV2[];
  clusters: ObservationClusterV2[];
  contextGraph?: GraphSnapshot | null;
  compatibilitySummary?: LatestBehavioralPattern | null;
}): BehavioralReadModel {
  const rankedThemes = rankCanonicalThemes(params.themes, params.clusters);
  const primaryTheme = rankedThemes[0];
  const normalizedEvidence = normalizeBehavioralEvidenceRefs({
    theme: primaryTheme,
    clusters: params.clusters,
    contextGraph: params.contextGraph,
    compatibilitySummary: params.compatibilitySummary ?? null,
  });
  const contradictions = buildContradictions({
    behaviorKey: primaryTheme.theme_name,
    clusters: params.clusters,
    evidenceRefs: normalizedEvidence.evidenceRefs,
  });
  const thinEvidence = isThinBehavioralEvidence({
    supportingArtifactCount: primaryTheme.supporting_artifact_count,
    evidenceRefCount: normalizedEvidence.evidenceRefs.length,
  });

  let confidence = normalizeThemeConfidence(primaryTheme);
  if (thinEvidence || contradictions.length > 0) {
    confidence = downgradeBehavioralConfidence(confidence);
  }

  return {
    primaryObservation: {
      behaviorKey: primaryTheme.theme_name,
      label: primaryTheme.theme_name,
      source: "bi_v2",
    },
    confidence,
    contradictions,
    evidenceRefs: normalizedEvidence.evidenceRefs,
    sourceSummary: {
      mode: "canonical_bi_v2",
      usedCompatibilityFallback: normalizedEvidence.usedCompatibilityFallback,
      thinEvidence,
      canonicalThemeCount: params.themes.length,
      graphEvidenceCount: normalizedEvidence.graphEvidenceCount,
    },
    supportSummary: {
      kind: "canonical",
      artifactCount: primaryTheme.supporting_artifact_count,
      meetingCount: primaryTheme.supporting_meeting_count,
      thin: thinEvidence,
    },
  };
}

function buildCompatibilityFallback(
  compatibilitySummary: LatestBehavioralPattern,
): BehavioralReadModel {
  const behaviorKey = compatibilitySummary.alignmentNote?.trim() || compatibilitySummary.clusterId;
  const evidenceRefs = buildCompatibilityEvidenceRefs(compatibilitySummary);

  return {
    primaryObservation: {
      behaviorKey,
      label: behaviorKey,
      source: "compatibility_only_v1",
    },
    confidence: normalizeBehavioralConfidence(
      compatibilitySummary.confidence === "High"
        ? "high"
        : compatibilitySummary.confidence === "Low"
          ? "low"
          : "medium",
      "medium",
    ),
    contradictions: [],
    evidenceRefs,
    sourceSummary: {
      mode: "compatibility_only_v1",
      usedCompatibilityFallback: true,
      thinEvidence: evidenceRefs.length <= 1,
      canonicalThemeCount: 0,
      graphEvidenceCount: 0,
    },
    supportSummary: {
      kind: "compatibility_only_v1",
      legacyMeetingCount: compatibilitySummary.scopeMeetings,
      thin: evidenceRefs.length <= 1,
    },
  };
}

function buildEmptyReadModel(): BehavioralReadModel {
  return {
    primaryObservation: {
      behaviorKey: "insufficient_behavioral_signal",
      label: "insufficient_behavioral_signal",
      source: "no_behavioral_data",
    },
    confidence: "low",
    contradictions: [],
    evidenceRefs: [],
    sourceSummary: {
      mode: "no_behavioral_data",
      usedCompatibilityFallback: false,
      thinEvidence: true,
      canonicalThemeCount: 0,
      graphEvidenceCount: 0,
    },
    supportSummary: {
      kind: "no_behavioral_data",
    },
  };
}

export function buildCoachVeeraBehavioralReadModel(
  params: BuildCoachVeeraBehavioralReadModelParams,
): BehavioralReadModel {
  const clusters = params.biClusters?.filter(Boolean) ?? [];
  const suppliedThemes = params.biThemes?.filter(Boolean) ?? [];
  const themes = suppliedThemes.filter(theme => !["contradicted", "rejected", "retired"].includes(theme.status)
    && theme.metadata?.suppressed !== true && theme.metadata?.is_suppressed !== true
    && theme.metadata?.contradiction_flag !== true && !(Number(theme.metadata?.contradiction_count ?? 0) > 0)
    && themeContradictionCount(theme.theme_name, clusters) === 0
    && !clusters.some(cluster => cluster.behavior_theme === theme.theme_name && cluster.metadata?.contradiction_flag === true));

  if (themes.length > 0) {
    return buildCanonicalReadModel({
      themes,
      clusters,
      contextGraph: params.contextGraph,
      compatibilitySummary: params.compatibilitySummary,
    });
  }

  return buildEmptyReadModel();
}

export function normalizeBehavioralEvidenceRefs(params: {
  theme: BehavioralThemeV2;
  clusters: ObservationClusterV2[];
  contextGraph?: GraphSnapshot | null;
  compatibilitySummary?: LatestBehavioralPattern | null;
}): NormalizedBehavioralEvidenceRefs {
  const graphEvidenceRefs = buildCanonicalEvidenceRefs({
    theme: params.theme,
    clusters: params.clusters,
    contextGraph: params.contextGraph,
  });
  if (graphEvidenceRefs.length > 0) {
    return {
      evidenceRefs: graphEvidenceRefs,
      graphEvidenceCount: graphEvidenceRefs.length,
      usedCompatibilityFallback: false,
    };
  }
  return {
    evidenceRefs: [],
    graphEvidenceCount: 0,
    usedCompatibilityFallback: false,
  };
}
